import { ApplicationConfig, inject, isDevMode, provideZoneChangeDetection, provideAppInitializer } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideFastSVG } from '@push-based/ngx-fast-svg';
import { authInterceptor, provideKeycloak } from '@wheere-front/shared-auth';
import { BUG_REPORT_APP_NAME, BUG_REPORT_BASE_PATH, LanguageService, provideTooltip } from '@wheere-front/shared-ui';
import { APP_ENVIRONMENT_KEYCLOAK } from '@wheere-front/shared-util';
import { KeycloakOptions } from 'keycloak-angular';
import { environment } from '../environments';
import { routes } from './app.routes';
import { providePrimeNG } from 'primeng/config';
import { PrimengPresetConfig } from '@wheere-front/shared-primeng-preset';
import { MessageService } from 'primeng/api';
import { provideServiceWorker } from '@angular/service-worker';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { CUSTOMER_APP_ENVIRONMENT } from '@wheere-front/customer-shared-util';
import { provideMaplibreWorker } from '@wheere-front/shared-map-libre/worker';
import { BASE_PATH as OPENAPI_SITE } from '@wheere/api-site';
import { BASE_PATH as OPENAPI_TRACKER } from '@wheere/api-tracker';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { configTransloco } from './core/transloco-config';

const keycloakOptions: KeycloakOptions = {
  config: {
    url: environment.keycloak.issuer,
    realm: environment.keycloak.realm,
    clientId: environment.keycloak.clientId,
  },
  loadUserProfileAtStartUp: true,
  initOptions: {
    onLoad: 'login-required',
    checkLoginIframe: false,
    pkceMethod: 'S256',
  },
  bearerExcludedUrls: ['/assets'],
  enableBearerInterceptor: true,
};

export const libConfig: ApplicationConfig = {
  providers: [
    provideMaplibreWorker(),
    { provide: CUSTOMER_APP_ENVIRONMENT, useValue: environment },
    { provide: OPENAPI_SITE, useValue: environment.api.site },
    // Position telemetry (history, last position) is owned by trackermanager —
    // sitemanager has a passthrough that 500s on schema mismatches (jsonb cols
    // mapped as String). Route the customer client straight to the source.
    { provide: OPENAPI_TRACKER, useValue: environment.api.tracker },
    { provide: APP_ENVIRONMENT_KEYCLOAK, useValue: environment.keycloak },
    // Bug-report wiring — shared button from libs/shared-ui posts to usermanager.
    // BUG_REPORT_BASE_PATH is shared-ui's own token; only provided when the env
    // declares an `api.user` URL so deploys without a usermanager fail clearly
    // instead of POSTing to a wrong URL.
    ...(environment.api.user
      ? [
          { provide: BUG_REPORT_BASE_PATH, useValue: environment.api.user },
          { provide: BUG_REPORT_APP_NAME, useValue: 'wheere-customer' },
        ]
      : []),
    { provide: MessageService },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: PrimengPresetConfig,
        options: {
          // Match the class set on <html> by the shared ThemeService so PrimeNG
          // components flip to dark automatically (Aura design tokens are
          // overridden in libs/shared-ui's _app-shell.scss).
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, tailwind-utilities,primeng',
          },
        },
      },
    }),
    provideKeycloak(keycloakOptions),
    provideTransloco(configTransloco),
    // Run LanguageService at app-init so the active language is applied to
    // Transloco AND the JSON dictionary is fetched before the first render —
    // otherwise the shell paints raw keys for one tick. Mirrors integrator.
    provideAppInitializer(() => {
      const initializerFn = (() => {
        const lang = inject(LanguageService);
        const transloco = inject(TranslocoService);
        return async () => {
          const active = lang.active();
          try {
            await firstValueFrom(transloco.load(active));
          } catch {
            // Loader will keep retrying lazily; pipes recover when JSON lands.
          }
        };
      })();
      return initializerFn();
    }),
    provideTooltip(),
    provideFastSVG({
      url: (name: string) => `assets/svg/${name}.svg`,
    }),
    // Service worker — registers in non-dev builds for offline-resilient field use.
    // The ngsw-config.json that already ships defines the asset groups. Dev mode
    // stays opt-out so hot reload + live source maps aren't intercepted by the SW.
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
